import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import project from '@data/project.json';
import poisData from '@data/pois.json';
import KhoudMap from '@components/KhoudMap';
import NewCustomerButton from '@components/NewCustomerButton';
import { useT } from '@lib/i18n';
import { useSessionStore } from '@stores/sessionStore';
import type { POI } from '@lib/types';

const GALLERY: ReadonlyArray<{ id: 'pool' | 'lobby' | 'gym' | 'parking'; src: string; labelKey: 'project.imgPool' | 'project.imgLobby' | 'project.imgGym' | 'project.imgParking' }> = [
  {
    id: 'pool',
    src: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=600&h=400&fit=crop',
    labelKey: 'project.imgPool',
  },
  {
    id: 'lobby',
    src: 'https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=600&h=400&fit=crop',
    labelKey: 'project.imgLobby',
  },
  {
    id: 'gym',
    src: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=400&fit=crop',
    labelKey: 'project.imgGym',
  },
  {
    id: 'parking',
    src: 'https://images.unsplash.com/photo-1506521781263-d8422e82f27a?w=600&h=400&fit=crop',
    labelKey: 'project.imgParking',
  },
];

export default function Step2Project() {
  const t = useT();
  const navigate = useNavigate();
  const language = useSessionStore((s) => s.language);
  const isAr = language === 'ar';

  const center: [number, number] = poisData.center as [number, number];
  const anchors = useMemo<POI[]>(
    () => (poisData.pois as POI[]).filter((p) => p.anchor),
    []
  );

  return (
    <div className="step-container">
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={() => navigate('/welcome')}
          className="btn-ghost !px-3 !py-1.5 text-sm"
        >
          {isAr ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
          {t('common.back')}
        </button>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 text-xs text-navy-400">
            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
            {t('project.timeBudget')}
            <span className="num-en">· 4 {t('common.minute')}</span>
          </span>
          <NewCustomerButton />
        </div>
      </div>

      <header className="text-center mb-8">
        <h1 className="text-3xl md:text-5xl font-bold text-navy mb-3">
          {isAr ? project.tagline_ar : project.tagline_en}
        </h1>
        <p className="text-base md:text-lg text-navy-600">
          {isAr ? project.hero_subtitle_ar : project.hero_subtitle_en}
        </p>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        <StatCard
          value={<span className="num-en">{project.totalUnits}</span>}
          label={t('project.units')}
        />
        <StatCard
          value={<span className="num-en">{project.floors}</span>}
          label={t('project.floors')}
        />
        <StatCard
          value={<span className="num-en">{project.shops}</span>}
          label={t('project.shops')}
        />
        <StatCard
          value={t('project.deliveryMar2029')}
          label={t('project.deliveryLabel')}
        />
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-bold text-navy mb-4">{t('project.mapTitle')}</h2>
        <KhoudMap
          center={center}
          pois={anchors}
          ariaLabel={t('project.mapAriaLabel')}
        />
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-bold text-navy mb-4">{t('project.galleryTitle')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {GALLERY.map((img) => (
            <figure
              key={img.id}
              className="card overflow-hidden p-0 group"
            >
              <div className="aspect-[3/2] overflow-hidden bg-navy-50">
                <img
                  src={img.src}
                  alt={t(img.labelKey)}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <figcaption className="px-3 py-2 text-sm font-medium text-navy text-center">
                {t(img.labelKey)}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-bold text-navy mb-4">{t('project.demandTitle')}</h2>
        <div className="grid gap-3 md:grid-cols-3">
          {project.demandClusters.map((cluster) => (
            <article key={cluster.name_en} className="card p-5">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-base font-bold text-navy">
                  {isAr ? cluster.name_ar : cluster.name_en}
                </h3>
                <span className="text-xs text-navy-400">
                  <span className="num-en">{cluster.anchor_count}</span>{' '}
                  {t('project.anchorsLabel')}
                </span>
              </div>
              <p className="text-sm text-navy-600 leading-relaxed">
                {isAr ? cluster.desc_ar : cluster.desc_en}
              </p>
            </article>
          ))}
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => navigate('/goal')}
          className="btn-primary"
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  );
}

function StatCard({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <div className="stat-card">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
